import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import { CAAC_URL } from "@polifonia/env";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { InfoBox } from "@/components/InfoBox";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { LineWebhookCheckModal } from "@/pages/channels/components/LineWebhookCheckModal";
import { useLineWebhookCheck } from "@/pages/channels/hooks/useLineWebhookCheck";

/**
 * Window name "back to CAAC" opens. A CAAC tab carrying this name is reused
 * instead of opening another one; without it, the browser opens a new tab
 * under that name.
 */
const CAAC_WINDOW_NAME = "caac";

const formatBasicId = (basicId: string) =>
  basicId.startsWith("@") ? basicId : `@${basicId}`;

const styles = {
  success: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    textAlign: "center",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface LineCreateSuccessProps {
  open: boolean;
  channel: InterludeTypes["LineChannelCreateResponse"];
  /** Leaves the flow; every way out of this view ends here. */
  onFinish: () => void;
}

/**
 * Result view of a connected LINE OA, with the webhook reminder on top when
 * LINE reports the webhook off.
 */
export const LineCreateSuccess: React.FC<LineCreateSuccessProps> =
  function LineCreateSuccess({ open, channel, onFinish }) {
    const { t } = useTranslation();
    const webhookCheck = useLineWebhookCheck({
      channelUuid: channel.uuid,
      initialActive: channel.webhookActive,
    });

    // `Button` does not take a link `target`, so the named window is opened
    // here. Reusing the name brings back a CAAC tab that is still open. An
    // environment without `VITE_CAAC_URL` would open a blank tab, so it only
    // finishes the flow.
    const handleBackToCaac = useHandler(() => {
      // No "noopener": named-window reuse needs the same browsing context
      // group, and both origins are first-party.
      if (CAAC_URL) window.open(CAAC_URL, CAAC_WINDOW_NAME);
      onFinish();
    });

    return (
      <>
        <Modal.Success
          open={open}
          handleClose={onFinish}
          title={t(
            "channels.line.create.success.title",
            "LINE Official Account connected",
          )}
          footer={
            <Stack direction="row" justifyContent="flex-end" gap="12px">
              <Button variant="plain" onClick={onFinish}>
                {t(
                  "channels.line.create.success.stay",
                  "Stay in Crescendo Lab Data Hub",
                )}
              </Button>
              <Button variant="primary" onClick={handleBackToCaac}>
                {t("channels.line.create.success.backToCaac", "Back to CAAC")}
              </Button>
            </Stack>
          }
        >
          <Box sx={styles.success} data-test="line-create-success">
            <Typography
              variant="body"
              fontWeight={700}
              color={theme.colors.staticFgTitle}
            >
              {t("channels.line.create.success.channelName", '"{{name}}"', {
                name: channel.name,
              })}
              {channel.basicId ? ` ${formatBasicId(channel.basicId)}` : null}
            </Typography>
            <InfoBox
              variant="warning"
              description={t(
                "channels.line.create.success.autoReplyReminder",
                "Reminder: Turn off auto-reply messages in LINE Official Account Manager so customers don't get two replies.",
              )}
            />
          </Box>
        </Modal.Success>
        <LineWebhookCheckModal
          open={open && webhookCheck.shouldAsk}
          basicId={channel.basicId}
          onClose={webhookCheck.dismiss}
          onRecheck={webhookCheck.recheck}
          rechecking={webhookCheck.rechecking}
          recheckResult={webhookCheck.recheckResult}
        />
      </>
    );
  };
